<script setup>
import { watch, ref } from 'vue';
import { getClient } from '@/core/js/GraphqlClient.js';
import BtnLoadMore from '@/components/btn-load-more/BtnLoadMore.vue';
import Loader from '@/components/loader/Loader.vue';
import { GA } from '@/mixins/Ga';
import { useStore } from '@/store/Store.js';
import { useLenis } from '@/components/scroll/Lenis.js';

const lenis = useLenis();

const props = defineProps({
    data: { type: Object, default: () => {} },
    color: { type: String, default: '' },
    mainColor: {
        type: String,
        default: '',
    },
});

const store = useStore();
const limit = ref(10);
const offset = ref(0);
const hasMore = ref(true);
const error = ref('');
const stores = ref([]);
const postcode = ref('');
const variables = { lang: store.lang.value };
const client = getClient();
let theLoc = { lat: null, long: null };
const gmap = ref(null);
const markerClusterer = ref(null);

const mapOpened = ref(true);
const center = { lat: 45.880105636313914, lng: -72.48861089371046 };
const openedMarkerID = ref(null);
const markers = ref([]);
const firstRun = ref(true);

const mapStyles = [
    {
        featureType: 'water',
        elementType: 'geometry',
        stylers: [
            {
                color: '#e9e9e9',
            },
            {
                lightness: 17,
            },
        ],
    },
    {
        featureType: 'landscape',
        elementType: 'geometry',
        stylers: [
            {
                color: '#f5f5f5',
            },
            {
                lightness: 20,
            },
        ],
    },
    {
        featureType: 'road.highway',
        elementType: 'geometry.fill',
        stylers: [
            {
                color: '#ffffff',
            },
            {
                lightness: 17,
            },
        ],
    },
    {
        featureType: 'road.highway',
        elementType: 'geometry.stroke',
        stylers: [
            {
                color: '#ffffff',
            },
            {
                lightness: 29,
            },
            {
                weight: 0.2,
            },
        ],
    },
    {
        featureType: 'road.arterial',
        elementType: 'geometry',
        stylers: [
            {
                color: '#ffffff',
            },
            {
                lightness: 18,
            },
        ],
    },
    {
        featureType: 'road.local',
        elementType: 'geometry',
        stylers: [
            {
                color: '#ffffff',
            },
            {
                lightness: 16,
            },
        ],
    },
    {
        featureType: 'poi',
        elementType: 'geometry',
        stylers: [
            {
                color: '#f5f5f5',
            },
            {
                lightness: 21,
            },
        ],
    },
    {
        featureType: 'poi.park',
        elementType: 'geometry',
        stylers: [
            {
                color: '#dedede',
            },
            {
                lightness: 21,
            },
        ],
    },
    {
        elementType: 'labels.text.stroke',
        stylers: [
            {
                visibility: 'on',
            },
            {
                color: '#ffffff',
            },
            {
                lightness: 16,
            },
        ],
    },
    {
        elementType: 'labels.text.fill',
        stylers: [
            {
                saturation: 36,
            },
            {
                color: '#333333',
            },
            {
                lightness: 40,
            },
        ],
    },
    {
        elementType: 'labels.icon',
        stylers: [
            {
                visibility: 'off',
            },
        ],
    },
    {
        featureType: 'transit',
        elementType: 'geometry',
        stylers: [
            {
                color: '#f2f2f2',
            },
            {
                lightness: 19,
            },
        ],
    },
    {
        featureType: 'administrative',
        elementType: 'geometry.fill',
        stylers: [
            {
                color: '#fefefe',
            },
            {
                lightness: 20,
            },
        ],
    },
    {
        featureType: 'administrative',
        elementType: 'geometry.stroke',
        stylers: [
            {
                color: '#fefefe',
            },
            {
                lightness: 17,
            },
            {
                weight: 1.2,
            },
        ],
    },
];

const createClusterIcon = (cluster) => {
    const count = cluster.getSize();

    // Define your SVG here. You can make it dynamic based on the count.
    const svg = `
    <svg width="62" height="62" viewBox="0 0 62 62" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M31 62C48.1208 62 62 48.1208 62 31C62 13.8792 48.1208 0 31 0C13.8792 0 0 13.8792 0 31C0 48.1208 13.8792 62 31 62ZM58.6439 30.5142C58.6439 46.0498 46.0498 58.6439 30.5142 58.6439C14.9787 58.6439 2.38462 46.0498 2.38462 30.5142C2.38462 14.9787 14.9787 2.38462 30.5142 2.38462C46.0498 2.38462 58.6439 14.9787 58.6439 30.5142Z" fill="#F1716F"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M31 58C45.9117 58 58 45.9117 58 31C58 16.0883 45.9117 4 31 4C16.0883 4 4 16.0883 4 31C4 45.9117 16.0883 58 31 58ZM31 49.6C41.2725 49.6 49.6 41.2725 49.6 31C49.6 20.7275 41.2725 12.4 31 12.4C20.7275 12.4 12.4 20.7275 12.4 31C12.4 41.2725 20.7275 49.6 31 49.6Z" fill="#F1716F"/>
<path d="M47 31C47 39.8366 39.8366 47 31 47C22.1634 47 15 39.8366 15 31C15 22.1634 22.1634 15 31 15C39.8366 15 47 22.1634 47 31Z" fill="#F1716F"/>
</svg>
  `;

    // Convert SVG string to Base64
    const encoded = window.btoa(svg);

    return {
        url: 'data:image/svg+xml;base64,' + encoded,
        height: 62,
        width: 62,
    };
};

const markerIconSvg =
    '<svg width="27" height="39" viewBox="0 0 27 39" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M13.562 39C0.722035 32.7955 -9.1072 0 13.562 0C36.2297 0 26.015 31.9091 13.562 39Z" fill="#F1716F"/></svg>';

// Convert SVG string to Base64
const markerIconSvgEcoded = window.btoa(markerIconSvg);

const markerIcon = {
    url: 'data:image/svg+xml;base64,' + markerIconSvgEcoded,
    scaledSize: { width: 27, height: 39 },
    labelOrigin: { x: 13, y: 17 },
};

const renderer = ({ count, position }) =>
    new google.maps.Marker({
        label: { text: String(count), color: 'white' },
        position,
        icon: createClusterIcon({ getSize: () => count }),
        // adjust zIndex to make sure the label is visible
        zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
    });

const call = async (loc = { lat: null, lng: null }) => {
    const { data: res } = await client.executeQuery({
        variables: {
            map: {
                coordinate: {
                    ...loc,
                },
                radius: 100000000,
            },
            order: 'distance',
            limit: limit.value,
            offset: offset.value,
            ...variables,
        },
        query: `
            query FetchEntries($lang: [String], $map: Ether_MapQuery, $limit: Int, $offset: Int, $order: String) {
                stores: entries (section: "grocery", site: $lang, type: "saleSpecialist", groceryMap: $map, orderBy: $order, limit: $limit, offset: $offset) {
                    title,
                    ... on saleSpecialist_Entry {
                        url: groceryUrl,
                        map: groceryMap {
                            distance,
                            address,
                            lat,
                            lng
                        }
                    }
                },
                total: entryCount (section: "grocery", site: $lang, type: "saleSpecialist", groceryMap: $map, orderBy: $order, limit: $limit, offset: $offset)
            }
        `,
        cachePolicy: 'cache-and-network',
    });

    // const json = await res.json();
    stores.value.push(...res.stores);
    hasMore.value = stores.value.length != res.total;
    isSearching.value = false;
    firstRun.value = false;

    const bounds = new google.maps.LatLngBounds();

    // link = json.meta.pagination.links.next;
    res.stores.forEach((item, index) => {
        bounds.extend(new google.maps.LatLng(item.map.lat, item.map.lng));

        markers.value.push({
            position: {
                lat: item.map.lat,
                lng: item.map.lng,
            },
            store: {
                item,
            },
            markerNum: (index + 1).toString(),
        });
    });

    const mymap = await gmap.value.$mapPromise;
    mymap.fitBounds(bounds);
    const newCenter = await getCenterWithOffset();
    mymap.setCenter(newCenter);
};

const getCenterWithOffset = async (coords = null) => {
    const offsety = 0.01;
    const offsetx = window.innerWidth >= 1024 ? window.innerWidth / -5 : 0;

    const mymap = await gmap.value.$mapPromise;

    let mapCenter = mymap.getCenter();
    let scale = Math.pow(2, mymap.getZoom());
    let latlng = new google.maps.LatLng(mapCenter.lat(), mapCenter.lng());
    if (coords !== null) {
        latlng = new google.maps.LatLng(coords.lat, coords.lng);
    }
    let worldCoordinateCenter = mymap.getProjection().fromLatLngToPoint(latlng);
    let pixelOffset = new google.maps.Point(offsetx / scale || 0, offsety / scale || 0);

    let worldCoordinateNewCenter = new google.maps.Point(worldCoordinateCenter.x - pixelOffset.x, worldCoordinateCenter.y + pixelOffset.y);
    let newCenter = mymap.getProjection().fromPointToLatLng(worldCoordinateNewCenter);

    return newCenter;
};

watch([offset], () => call(theLoc));
const isSearching = ref(false);

const more = () => {
    offset.value += limit.value;
};

let postcodeCache = null;

const ga = new GA({ category: 'midBody', action: 'clicExterne' });

const scrolledOnce = ref(false);
const search = async () => {
    if (isSearching.value || postcode.value === postcodeCache) return;

    postcodeCache = postcode.value;

    error.value = false;

    if (!/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(postcode.value)) error.value = 'error';

    if (postcode.value.replace(/ /g, '').length < 6) error.value = 'incomplete';

    if (postcode.value == '') error.value = 'empty';

    if (error.value) return;

    error.value = null;

    isSearching.value = true;

    if (postcode.value != '') {
        let url = 'https://maps.googleapis.com/maps/api/geocode/json';
        url += `?address=${postcode.value}`;
        url += '&key=AIzaSyDLdfOZu7bqwSFFhBKSJM0r4sy3Pwz4b3U';
        const res = await fetch(url);
        const json = await res.json();
        if (json.results.length > 0) {
            theLoc = json.results[0].geometry.location;
        }
    }

    ga.send({ label: postcode.value, camelCase: false });
    stores.value.length = 0;
    call(theLoc);
    if (!scrolledOnce.value) {
        lenis.value.scrollTo('#map-wrapper', {
            offset: -200,
        });
        scrolledOnce.value = true;
    }
};

// const { data } = await client.executeQuery({
//     variables,
//     query: `
//         query FetchEntries($lang: [String]) {
//             location: entry (section: "location", site: $lang) {
//                 ... on location_Entry {
//                     placeholder: locationSaleSpecialistPlaceHolder,
//                     ctaMap: locationCtaMap,
//                     error: locationSaleSpecialistErrorError,
//                     incomplete: locationSaleSpecialistErrorIncomplete,
//                     empty: locationSaleSpecialistErrorEmpty
//                 }
//             },
//         }
//     `,
// });

const openMarker = async (id) => {
    if (!gmap.value) return;

    // If clicking the same marker that's already open, just return
    if (openedMarkerID.value === id) {
        return;
    }

    // Wait for map to be ready
    const map = await gmap.value.$mapPromise;

    const marker = markers.value[id];
    if (!marker) return;

    // Define zoom levels
    const defaultMaxZoom = 15; // Reduced max zoom for better usability
    const defaultViewZoom = 13; // Standard zoom level for viewing markers

    // Check if marker would be clustered at current zoom
    const pixelBounds = map.getBounds();
    if (pixelBounds && markerClusterer.value) {
        const currentZoom = map.getZoom();

        // Only zoom in if we're too far out
        if (currentZoom < defaultViewZoom) {
            // Set to our standard viewing zoom level
            await new Promise((resolve) => {
                map.setZoom(defaultViewZoom);
                // Wait for zoom animation to complete
                google.maps.event.addListenerOnce(map, 'idle', resolve);
            });
        }

        // Small delay to ensure cluster has disbanded
        await new Promise((resolve) => setTimeout(resolve, 300));
    }

    // Get the marker's position as LatLng
    const markerPosition = await getCenterWithOffset(marker.position);
    // Just pan to the marker if we're already zoomed in enough
    map.panTo(markerPosition);

    // Now open the info window
    openedMarkerID.value = id;
};
</script>

<template>
    <div class="LocationSaleSpecialist pb-12 -mx-6 lg:-mx-20">
        <div class="bg-pink w-full">
            <div class="bg-light-blue py-5 px-6 lg:px-22 lg:flex justify-between">
                <div class="text-white flex items-center mb-4 lg:mb-0">
                    <button
                        v-on:click="mapOpened = true"
                        class="rounded-l-lg inline-block w-17 h-12 hover:bg-dark-blue"
                        :class="{
                            'bg-dark-blue': mapOpened,
                            'bg-darker-blue': !mapOpened,
                        }"
                    >
                        <svg
                            class="h-6"
                            viewBox="0 0 19 33"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M7.10938 20.1183V30.0644L8.50713 32.1604C8.80864 32.6124 9.47324 32.6124 9.77476 32.1604L11.1719 30.0644V20.1183C10.5124 20.2401 9.83506 20.3125 9.14062 20.3125C8.44619 20.3125 7.7689 20.2401 7.10938 20.1183ZM9.14062 0C4.09233 0 0 4.09233 0 9.14063C0 14.1889 4.09233 18.2813 9.14062 18.2813C14.1889 18.2813 18.2812 14.1889 18.2812 9.14063C18.2812 4.09233 14.1889 0 9.14062 0ZM9.14062 4.82422C6.76025 4.82422 4.82422 6.76025 4.82422 9.14063C4.82422 9.56084 4.48271 9.90234 4.0625 9.90234C3.64229 9.90234 3.30078 9.56084 3.30078 9.14063C3.30078 5.92046 5.92109 3.30078 9.14062 3.30078C9.56084 3.30078 9.90234 3.64229 9.90234 4.0625C9.90234 4.48272 9.56084 4.82422 9.14062 4.82422Z"
                                fill="#E6F5FD"
                            />
                        </svg>
                    </button>
                    <button
                        v-on:click="mapOpened = false"
                        class="rounded-r-lg inline-block w-17 h-12 hover:bg-dark-blue"
                        :class="{
                            'bg-dark-blue': !mapOpened,
                            'bg-darker-blue': mapOpened,
                        }"
                    >
                        <svg
                            class="h-5"
                            viewBox="0 0 33 27"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M5.07812 20.3125H1.01562C0.746265 20.3125 0.487936 20.4195 0.29747 20.61C0.107003 20.8004 0 21.0588 0 21.3281L0 25.3906C0 25.66 0.107003 25.9183 0.29747 26.1088C0.487936 26.2992 0.746265 26.4062 1.01562 26.4062H5.07812C5.34749 26.4062 5.60581 26.2992 5.79628 26.1088C5.98675 25.9183 6.09375 25.66 6.09375 25.3906V21.3281C6.09375 21.0588 5.98675 20.8004 5.79628 20.61C5.60581 20.4195 5.34749 20.3125 5.07812 20.3125ZM5.07812 0H1.01562C0.746265 0 0.487936 0.107003 0.29747 0.29747C0.107003 0.487936 0 0.746265 0 1.01562L0 5.07812C0 5.34749 0.107003 5.60581 0.29747 5.79628C0.487936 5.98675 0.746265 6.09375 1.01562 6.09375H5.07812C5.34749 6.09375 5.60581 5.98675 5.79628 5.79628C5.98675 5.60581 6.09375 5.34749 6.09375 5.07812V1.01562C6.09375 0.746265 5.98675 0.487936 5.79628 0.29747C5.60581 0.107003 5.34749 0 5.07812 0ZM5.07812 10.1562H1.01562C0.746265 10.1562 0.487936 10.2633 0.29747 10.4537C0.107003 10.6442 0 10.9025 0 11.1719L0 15.2344C0 15.5037 0.107003 15.7621 0.29747 15.9525C0.487936 16.143 0.746265 16.25 1.01562 16.25H5.07812C5.34749 16.25 5.60581 16.143 5.79628 15.9525C5.98675 15.7621 6.09375 15.5037 6.09375 15.2344V11.1719C6.09375 10.9025 5.98675 10.6442 5.79628 10.4537C5.60581 10.2633 5.34749 10.1562 5.07812 10.1562ZM31.4844 21.3281H11.1719C10.9025 21.3281 10.6442 21.4351 10.4537 21.6256C10.2633 21.8161 10.1562 22.0744 10.1562 22.3438V24.375C10.1562 24.6444 10.2633 24.9027 10.4537 25.0932C10.6442 25.2836 10.9025 25.3906 11.1719 25.3906H31.4844C31.7537 25.3906 32.0121 25.2836 32.2025 25.0932C32.393 24.9027 32.5 24.6444 32.5 24.375V22.3438C32.5 22.0744 32.393 21.8161 32.2025 21.6256C32.0121 21.4351 31.7537 21.3281 31.4844 21.3281ZM31.4844 1.01562H11.1719C10.9025 1.01562 10.6442 1.12263 10.4537 1.31309C10.2633 1.50356 10.1562 1.76189 10.1562 2.03125V4.0625C10.1562 4.33186 10.2633 4.59019 10.4537 4.78065C10.6442 4.97112 10.9025 5.07812 11.1719 5.07812H31.4844C31.7537 5.07812 32.0121 4.97112 32.2025 4.78065C32.393 4.59019 32.5 4.33186 32.5 4.0625V2.03125C32.5 1.76189 32.393 1.50356 32.2025 1.31309C32.0121 1.12263 31.7537 1.01562 31.4844 1.01562ZM31.4844 11.1719H11.1719C10.9025 11.1719 10.6442 11.2789 10.4537 11.4693C10.2633 11.6598 10.1562 11.9181 10.1562 12.1875V14.2188C10.1562 14.4881 10.2633 14.7464 10.4537 14.9369C10.6442 15.1274 10.9025 15.2344 11.1719 15.2344H31.4844C31.7537 15.2344 32.0121 15.1274 32.2025 14.9369C32.393 14.7464 32.5 14.4881 32.5 14.2188V12.1875C32.5 11.9181 32.393 11.6598 32.2025 11.4693C32.0121 11.2789 31.7537 11.1719 31.4844 11.1719Z"
                                fill="#E6F5FD"
                            />
                        </svg>
                    </button>
                </div>
                <div class="LocationSaleSpecialist__form flex w-full shrink-0 transition duration-300 xl:w-85">
                    <input
                        v-model="postcode"
                        class="LocationSaleSpecialist__input w-full border-none bg-white py-3 px-5 rounded-l-lg font-sans text-lg font-normal uppercase transition duration-300 placeholder:normal-case placeholder:text-black/20 placeholder:transition placeholder:duration-300 focus:placeholder:opacity-50"
                        :class="{ 'text-dark-red placeholder:text-dark-red': error }"
                        :placeholder="data.data.locationSaleSpecialistPlaceHolder"
                        type="text"
                        v-on:keyup.enter="search"
                    />
                    <button
                        class="LocationSaleSpecialist__submit flex items-center shrink-0 cursor-pointer font-bold py-3 px-3 lg:px-5 rounded-r-lg bg-darker-blue hover:bg-dark-blue text-white p-0 text-lg"
                        :class="{
                            '!bg-dark-blue hover:!bg-dark-blue': isSearching,
                        }"
                        v-on:click="search"
                    >
                        <span
                            v-if="!isSearching"
                            class="inline-block w-26 text-center"
                            >Rechercher</span
                        >
                        <span
                            v-if="isSearching"
                            class="inline-flex items-center justify-center w-26 text-center"
                        >
                            <Loader
                                bg="transparent"
                                size="small"
                                line="white"
                            />
                        </span>
                    </button>
                </div>
            </div>
            <div
                id="map-wrapper"
                class="bg-dark-blue text-white h-screen lg:h-[80vh] relative overflow-hidden"
                :class="{
                    block: mapOpened,
                    hidden: !mapOpened,
                }"
            >
                <GMapMap
                    ref="gmap"
                    :center="center"
                    :zoom="7"
                    class="h-full w-full"
                    :options="{
                        styles: mapStyles,
                    }"
                >
                    <GMapCluster
                        ref="markerClusterer"
                        :renderer="{ render: renderer }"
                    >
                        <GMapMarker
                            :key="index"
                            v-for="(m, index) in markers"
                            :position="m.position"
                            :clickable="true"
                            @click="openMarker(index)"
                            :icon="markerIcon"
                            :label="{
                                text: m.markerNum,
                                color: '#FFFFFF',
                                fontSize: '16px',
                                fontWeight: 'bold',
                            }"
                        >
                            <GMapInfoWindow
                                :closeclick="true"
                                @closeclick="openedMarkerID = null"
                                :opened="openedMarkerID === index"
                            >
                                <div class="text-dark-blue font-bold text-sm">
                                    {{ m.store.item.title }}
                                </div>
                                <div class="text-[#4B5563]">
                                    {{ m.store.item.map.address }}
                                </div>
                                <div class="flex justify-between flex-col w-full">
                                    <a
                                        class="LocationSaleSpecialist__link mt-4 flex items-center justify-between"
                                        :href="'https://www.google.ca/maps/place/' + m.store.item.map.address + '/'"
                                        target="_blank"
                                        v-on:click="ga.send({ label: m.store.item.map.address, camelCase: false })"
                                    >
                                        <div
                                            v-if="m.store.item.map.distance"
                                            class="LocationSaleSpecialist__distance font-light text-[#4B5563]"
                                        >
                                            {{ m.store.item.map.distance.toFixed(1).replace('.', ',') }} km
                                        </div>
                                        <Btn
                                            color="currentColor"
                                            size="small"
                                            text="Directions"
                                            :up="true"
                                        />
                                    </a>
                                </div>
                            </GMapInfoWindow>
                        </GMapMarker>
                    </GMapCluster>
                </GMapMap>
                <template v-if="stores.length > 0 && !isSearching">
                    <div
                        data-lenis-prevent
                        class="LocationSaleSpecialist__stores [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:rounded-r-lg [&::-webkit-scrollbar-thumb]:bg-black/10 [&::-webkit-scrollbar-thumb]:rounded-full hidden lg:flex lg:flex-col absolute right-22 top-[10vh] w-1/3 overflow-auto h-[60vh] p-2 rounded-lg bg-white shadow-lg"
                    >
                        <button
                            v-for="(store, storeIndex) in stores"
                            v-on:click.prevent="openMarker(storeIndex)"
                            class="LocationSaleSpecialist__card text-left hover:bg-black/5 rounded border-b border-black/10 last:border-none flex h-full items-start justify-between p-2 text-dark-blue"
                        >
                            <div class="mr-3 mt-1">
                                <svg
                                    class="w-4"
                                    viewBox="0 0 27 40"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M13.5622 39.2031C0.722279 32.9986 -9.10695 0.20311 13.5622 0.20311C36.23 0.20311 26.0152 32.1122 13.5622 39.2031Z"
                                        fill="#F1716F"
                                    />
                                    <text
                                        fill="white"
                                        x="13.5"
                                        y="17"
                                        style="white-space: pre"
                                        font-size="16"
                                        font-weight="bold"
                                        text-anchor="middle"
                                        alignment-baseline="central"
                                        letter-spacing="0em"
                                    >
                                        {{ storeIndex + 1 }}
                                    </text>
                                </svg>
                            </div>
                            <div>
                                <div class="LocationSaleSpecialist__top">
                                    <h5 class="text-lg font-bold">
                                        {{ store.title }}
                                    </h5>
                                </div>
                                <div class="flex text-[#4B5563]">
                                    <div
                                        class="LocationSaleSpecialist__address text-sm"
                                        v-html="store?.map?.address ?? ''"
                                    />
                                    <div
                                        v-if="store.map.distance"
                                        class="LocationSaleSpecialist__distance text-[14px] w-16 ml-6 font-light"
                                    >
                                        {{ store.map.distance.toFixed(1).replace('.', ',') }} km
                                    </div>
                                </div>
                            </div>
                        </button>
                    </div>
                </template>
            </div>
            <div
                class="mx-auto max-w-5xl px-6 xl:px-20 py-12"
                :class="{
                    hidden: mapOpened,
                    block: !mapOpened,
                }"
            >
                <div class="h-12 flex items-start justify-center w-full">
                    <Loader
                        v-if="isSearching"
                        bg="white"
                        line="dark-blue"
                    />
                </div>
                <template v-if="stores.length > 0 && !isSearching">
                    <div class="LocationSaleSpecialist__stores grid gap-6 xl:grid-cols-2 xl:gap-12">
                        <div
                            v-for="(store, storeIndex) in stores"
                            class="LocationSaleSpecialist__card flex h-full flex-col justify-between rounded-xl bg-white p-6 text-dark-blue xl:p-8"
                        >
                            <div class="LocationSaleSpecialist__top">
                                <h5 class="h5">
                                    {{ store.title }}
                                </h5>
                                <p
                                    class="LocationSaleSpecialist__address"
                                    v-html="store?.map?.address ?? ''"
                                />
                            </div>
                            <a
                                v-if="store?.map?.address"
                                class="LocationSaleSpecialist__link mt-4 block border-t-2 border-dark-blue pt-6 xl:pt-8"
                                :href="'https://www.google.ca/maps/place/' + store.map.address + '/'"
                                target="_blank"
                                v-on:click="ga.send({ label: store.map.address, camelCase: false })"
                            >
                                <div class="wrapper-distance flex gap-4 items-center mb-4">
                                    <svg
                                        class="w-4 h-auto inline-block"
                                        viewBox="0 0 27 40"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            clip-rule="evenodd"
                                            d="M13.5622 39.2031C0.722279 32.9986 -9.10695 0.20311 13.5622 0.20311C36.23 0.20311 26.0152 32.1122 13.5622 39.2031Z"
                                            fill="#F1716F"
                                        />
                                        <text
                                            fill="white"
                                            x="13.5"
                                            y="17"
                                            style="white-space: pre"
                                            font-size="16"
                                            font-weight="bold"
                                            text-anchor="middle"
                                            alignment-baseline="central"
                                            letter-spacing="0em"
                                        >
                                            {{ storeIndex + 1 }}
                                        </text>
                                    </svg>
                                    <div
                                        v-if="store.map.distance"
                                        class="LocationSaleSpecialist__distance text-3xl font-medium"
                                    >
                                        {{ store.map.distance.toFixed(1).replace('.', ',') }} km
                                    </div>
                                </div>
                                <Btn
                                    color="currentColor"
                                    size="small"
                                    :text="data.data.locationCtaMap"
                                    :up="true"
                                />
                            </a>
                        </div>
                    </div>
                </template>
                <template v-if="firstRun">
                    <p class="text-center text-lg opacity-50">Veuillez entrer un code postal pour initier la recherche.</p>
                </template>
            </div>
        </div>
    </div>
</template>
